'use client';

import type { SignUpRequest } from '@/types';

import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  AgentComplete,
  AgentPrimaryForm,
  AgentRuleForm,
  AgentSecondaryForm,
  AgentTertiaryForm,
  CompleteForeignUpload,
  CustomerComplete,
  CustomerPrimaryForm,
  EnterPasswordForm,
  ForeignSecondarySignUpForm,
  MobileForm,
  ReferralForm,
  SubmitCodeForm,
} from '../_components';

import { Stack } from '@/components';
import { useGetExclusiveLink, useSignUp } from '@/hooks';
import useIsAdmin from '@/hooks/store/useIsAdmin';
import { setCookie, setLocalStorage } from '@/utils';

const SignUpFlow: React.FC<{ initialType?: SignUpRequest['type'] }> = ({
  initialType,
}) => {
  const [signUpData, setSignUpData] = React.useState<Partial<SignUpRequest>>(
    {}
  );
  const [{ canSkip, otpTime }, setOtpOption] = React.useState<{
    canSkip: boolean;
    otpTime: number;
  }>({ canSkip: false, otpTime: 0 });

  const toggleUserType = useIsAdmin(
    useShallow((state) => state.toggleUserType)
  );

  const [navigateLoading, setNavigateLoading] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState<
    | 'mobile'
    | 'submit_code'
    | 'enter_password'
    | 'referral_code'
    | 'agent_primary_data'
    | 'agent_secondary_data'
    | 'foreign_agent_secondary_data'
    | 'agent_tertiary_data'
    | 'agent_rules'
    | 'foreign_agent_complete'
    | 'agent_complete'
    | 'customer_primary_data'
    | 'customer_complete'
  >('mobile');

  const { mutateAsync: signUp, isPending: submitLoading } = useSignUp();

  const signUpHandler = (payload: Partial<SignUpRequest>) => {
    setNavigateLoading(true);
    void signUp(payload)
      .then(async ({ token }) => {
        await setCookie({ name: 'lia-token', value: token });
        setLocalStorage({ name: 'lia-token', value: token });
        toggleUserType(false);
        if (status === 'agent_rules') {
          if (payload.is_foreigner) {
            setStatus('customer_complete');
          } else {
            setStatus('agent_complete');
          }
        }
        if (status === 'customer_primary_data') {
          setStatus('customer_complete');
        }
      })
      .catch(() => setNavigateLoading(false));
  };

  useGetExclusiveLink({
    setSignUpData,
    enabled: status === 'enter_password',
    dependencies: [status],
  });

  const inView = {
    mobile: (
      <MobileForm
        initialType={initialType}
        goNext={({ data, canSkip: activeSkip, otpTime: duration }) => {
          setSignUpData((prev) => ({ ...prev, ...data }));
          setOtpOption({ canSkip: activeSkip, otpTime: duration });
          setStatus('submit_code');
        }}
      />
    ),
    submit_code: (
      <SubmitCodeForm
        goNext={(data) => {
          setSignUpData((prev) => ({
            ...prev,
            ...data,
          }));
          setStatus('enter_password');
        }}
        mobile={signUpData.mobile || ''}
        country_code_prefix={signUpData.country_phone_prefix || '+98'}
        canSkip={signUpData.country_phone_prefix !== '+98' && canSkip}
        otpTime={otpTime}
      />
    ),
    enter_password: (
      <EnterPasswordForm
        goNext={(data) => {
          setSignUpData((prev) => ({ ...prev, ...data }));
          setStatus('referral_code');
        }}
      />
    ),
    referral_code: (
      <ReferralForm
        goNext={(data) => {
          setSignUpData((prev) => ({ ...prev, ...data }));
          if (signUpData.type === 'agent') {
            setStatus('agent_primary_data');
          } else {
            setStatus('customer_primary_data');
          }
        }}
        csrfToken={signUpData.token || ''}
        parentId={signUpData.parent_id}
        registerType={signUpData.registration_type}
        hasReferralCode={signUpData.hasReferralCode || false}
      />
    ),
    agent_primary_data: (
      <AgentPrimaryForm
        goNext={(data) => {
          setSignUpData((prev) => ({ ...prev, ...data }));
          if (data.is_foreigner) {
            setStatus('foreign_agent_secondary_data');
          } else {
            setStatus('agent_secondary_data');
          }
        }}
        goBack={() => setStatus('referral_code')}
        data={signUpData}
      />
    ),
    agent_secondary_data: (
      <AgentSecondaryForm
        goNext={(data) => {
          setSignUpData((prev) => ({ ...prev, ...data }));
          setStatus('agent_tertiary_data');
        }}
        goBack={() => setStatus('agent_primary_data')}
        data={signUpData}
        csrfToken={signUpData.token || ''}
      />
    ),
    foreign_agent_secondary_data: (
      <ForeignSecondarySignUpForm
        goBack={() => setStatus('agent_primary_data')}
        goNext={(data) => {
          setSignUpData((prev) => ({ ...prev, ...data }));
          setStatus('agent_tertiary_data');
        }}
        data={signUpData}
        csrfToken={signUpData.token || ''}
      />
    ),
    agent_tertiary_data: (
      <AgentTertiaryForm
        goNext={(data) => {
          setSignUpData((prev) => ({ ...prev, ...data }));
          setStatus('agent_rules');
        }}
        goBack={() => {
          if (signUpData.is_foreigner) {
            setStatus('foreign_agent_secondary_data');
          } else {
            setStatus('agent_secondary_data');
          }
        }}
        data={signUpData}
      />
    ),
    agent_rules: (
      <AgentRuleForm
        onSubmit={() => signUpHandler(signUpData)}
        submitLoading={submitLoading || navigateLoading}
        goBack={() => setStatus('agent_tertiary_data')}
      />
    ),
    foreign_agent_complete: <CompleteForeignUpload />,
    agent_complete: <AgentComplete firstName={signUpData.first_name || ''} />,
    customer_primary_data: (
      <CustomerPrimaryForm
        goBack={() => {
          setSignUpData((prev) => ({ ...prev, parent_id: undefined }));
          setStatus('referral_code');
        }}
        data={signUpData}
        onSubmit={(data) => {
          setSignUpData({ ...signUpData, ...data });
          signUpHandler({ ...signUpData, ...data });
        }}
        submitLoading={submitLoading || navigateLoading}
      />
    ),
    customer_complete: (
      <CustomerComplete firstName={signUpData.first_name || ''} />
    ),
  };

  return (
    <Stack flex={1} w="full" h="full" align="center">
      {inView[status]}
    </Stack>
  );
};

export default SignUpFlow;
